// The fake api's Clavian Boot routes (F-BOOT-01 … F-BOOT-21), in the contract's shapes — the venue's boot server, images and
// versions, boot PCs, edits, wipes, rollouts and health (`packages/contracts/src/routes/boot.ts`):
//
//   GET   …/boot/servers                      listBootServers          boot:view
//   POST  …/boot/servers/pairing-code         createBootServerPairingCode  boot:manage
//   PATCH …/boot/servers/{id}                 updateBootServer         boot:manage
//   POST  …/boot/servers/{id}/unpair          unpairBootServer         boot:manage
//   POST  …/boot/servers/{id}/update          updateBootServerSoftware boot:manage   (409 with no update waiting)
//   GET   …/boot/images                       listBootImages           boot:view
//   POST  …/boot/images                       createBootImage          boot:manage
//   PATCH …/boot/images/{id}                  updateBootImage          boot:manage
//   GET   …/boot/images/{id}/versions         listBootImageVersions    boot:view
//   POST  …/boot/images/{id}/restore | merge  restoreBootImage, mergeBootImage  boot:manage
//   POST  …/boot/images/{id}/prepare          prepareBootImageVersion  boot:manage   (409 unless the version failed or is a published one with no parent)
//   POST  …/boot/images/{id}/edits            startBootEdit            boot:edit_image
//   POST  …/boot/images/{id}/server-edits     startBootServerEdit      boot:edit_image  (game disks; opens mounted)
//   POST  …/boot/edits/{id}/publish | discard publishBootEdit, discardBootEdit  boot:edit_image
//   GET   …/boot/pcs   PATCH …/pcs/{id}       listBootPcs, updateBootPc  boot:view, boot:manage
//   GET   …/boot/floor                        getBootFloor             boot:view     (each linked PC's boot state, derived)
//   GET   …/boot/floor-states                 getBootFloorStates       floor:read    (the same state and reason, nothing else)
//   GET   …/boot/classes  PUT …/classes/{id}  listBootClasses, updateBootClass  boot:view; boot:manage + stations:manage
//   POST  …/boot/images/{id}/make-independent makeBootImageIndependent boot:manage
//   POST  …/boot/pcs/{id}/remove              removeBootPc             boot:manage
//   POST  …/boot/pcs/{id}/shader-seed         promoteBootShaderSeed    boot:manage
//   POST  …/boot/wipes                        createBootWipe           boot:wipe
//   POST  …/boot/rollouts/restart-idle        restartIdleBootPcs       boot:manage
//   GET   …/boot/metrics                      getBootMetrics           boot:view
//
// Without the organisation's `boot` add-on (`boot: 'unentitled'`) every route answers `402 entitlement-required`, as the
// server's guard does. Bodies are checked with the contract's own schemas. Sizes are bytes, latencies microseconds.

import type { BootDisk, BootFloorPc, BootImage, BootImageVersion, BootMetricPoint, BootNewPc, BootPc, BootServer, Problem, Station } from '@clavian/api-client';
import {
  CreateBootImageSchema,
  UpdateBootClassSchema,
  CreateBootServerPairingCodeSchema,
  CreateBootWipeSchema,
  IdentifyBootPcSchema,
  MakeBootPcStationSchema,
  MergeBootImageSchema,
  PrepareBootImageVersionSchema,
  PublishBootEditSchema,
  RestartIdleBootPcsSchema,
  RestoreBootImageSchema,
  StartBootEditSchema,
  UpdateBootImageSchema,
  UpdateBootPcSchema,
  UpdateBootServerSchema,
  UpdateBootServerSoftwareSchema,
} from '@clavian/contracts';

import type { FakeResponse } from './fake-server';
import { FLOOR_CLASSES, floorStationId } from './fake-venue-floor';

const ORGANIZATION_ID = 'org_01k5znw4hmfay8vvr14d2pf2db';
const VENUE_ID = 'ven_01k5znw4hmfay8vvr14d2pf2dc';
const BASE = `/v1/organizations/${ORGANIZATION_ID}/venues/${VENUE_ID}/boot`;
const GB = 1024 ** 3;
/** The fixtures' "now": the moment the fake was loaded, so relative times ("2 minutes ago") read right on any day. */
const NOW_MS = Date.now();
const NOW = new Date(NOW_MS).toISOString();
const MINUTE_MS = 60_000;

const tid = (prefix: string, serial: number): string => `${prefix}_01k5znw4hmfay8vvr14d2g${String(serial).padStart(4, '0')}`.slice(0, prefix.length + 27);

export const BOOT_SERVER_ID = tid('bsv', 1);
export const BOOT_SYSTEM_IMAGE_ID = tid('bim', 1);
export const BOOT_GAME_IMAGE_ID = tid('bim', 2);
/** A system image still being captured: nothing published, so a PC given it cannot start (Station 5 on the Floor). */
export const BOOT_DRAFT_IMAGE_ID = tid('bim', 3);
/** Saved from version 2 of Windows 11 Gaming ("Save as a new image"): it stands on it, and the Pro class starts it. */
export const BOOT_TOURNAMENT_IMAGE_ID = tid('bim', 4);
/** A second game disk, on H:, for a class's own choice of games. */
export const BOOT_SECOND_GAME_IMAGE_ID = tid('bim', 5);
/** The motherboard Windows 11 Gaming was captured on, and the one PC 3 and the second new PC have. */
export const BOOT_IMAGE_BOARD = { manufacturer: 'ASUSTeK COMPUTER INC.', product: 'PRIME B650M-A' } as const;
export const BOOT_OTHER_BOARD = { manufacturer: 'Micro-Star International Co., Ltd.', product: 'MAG B760M MORTAR' } as const;
export const BOOT_PC_IDS = Array.from({ length: 8 }, (_, index) => tid('bpc', index + 1));
const MEMBER_ID = 'mem_01k5znw4hmfay8vvr14d2pf2dd';

export type BootFakeMode = 'ready' | 'unentitled' | 'empty';

export interface BootHelpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
  readonly gate: () => FakeResponse | undefined;
  readonly can: (permission: string) => boolean;
  /** Make it station: the floor fake draws a new station (absent without a live floor). */
  readonly makeStation?: (spec: { number: number; classId: string; zoneId?: string; name?: string }) => { station: Station } | { conflict: string } | { missing: string } | undefined;
  /** A Boot-only venue (no Clavian Stations): a PC for each of the floor's thirteen PCs (never its consoles). */
  readonly bootOnly?: boolean;
  /** The class of a station on the floor (a PC boots its own image, else its class's, else the venue default). */
  readonly classOf?: (stationId: string) => string | undefined;
}

/** The Pro class: it starts Windows 11 Tournament. */
const PRO_CLASS_ID = FLOOR_CLASSES[1]?.id ?? '';

const hoursAgo = (hours: number): string => new Date(NOW_MS - hours * 3_600_000).toISOString();

const REPORT = {
  steps: [
    { id: 'uefi-boot-files', name: 'UEFI boot files', status: 'ok', changes: ['\\EFI\\Microsoft\\Boot\\bootmgfw.efi', 'BCD store rebuilt'] },
    { id: 'hardware-binding', name: 'Hardware binding stripped', status: 'ok', changes: ['SYSTEM hive: enumerated devices cleared'] },
    { id: 'nic-drivers', name: 'Network drivers', status: 'ok', changes: ['Intel I225 (8086:125C)', 'Realtek RTL8125 (10EC:8125)'] },
    { id: 'network-at-boot', name: 'Network at boot', status: 'ok', changes: ['DisableDHCPMediaSense = 1'] },
    { id: 'clavian-components', name: 'Clavian components', status: 'ok', changes: ['Cache driver registered', 'Boot client service installed'] },
    { id: 'station-client', name: 'Station client', status: 'ok', changes: [] },
    { id: 'anti-cheat', name: 'Anti-cheat filters', status: 'ok', changes: [] },
    { id: 'pagefile', name: 'Page file and hibernation', status: 'ok', changes: ['Page file moved to write-back', 'Hibernation off', 'Fast startup off'] },
  ],
};

function seedServer(mode: BootFakeMode): BootServer[] {
  if (mode === 'empty') return [];
  const disks: BootDisk[] = [
    { role: 'system', path: 'C:\\', totalBytes: 480 * GB, freeBytes: 312 * GB },
    { role: 'images', path: 'D:\\ClavianBoot\\images', totalBytes: 3840 * GB, freeBytes: 2910 * GB },
    { role: 'games', path: 'E:\\ClavianBoot\\games', totalBytes: 7680 * GB, freeBytes: 640 * GB },
    { role: 'writeback', path: 'F:\\ClavianBoot\\writeback', totalBytes: 1920 * GB, freeBytes: 1620 * GB },
    { role: 'shader', path: 'F:\\ClavianBoot\\shader', totalBytes: 1920 * GB, freeBytes: 1620 * GB },
  ];
  return [
    {
      id: BOOT_SERVER_ID,
      venueId: VENUE_ID,
      name: 'Boot server',
      status: 'online',
      version: '1.0.3',
      pairedAt: hoursAgo(24 * 9),
      lastSeenAt: hoursAgo(0.01),
      disks,
      licenceState: 'valid',
      ramCacheBytes: 96 * GB,
      // Its own software: a newer version downloaded and checked, waiting for Update now, with six PCs booted.
      update: {
        installedVersion: '1.0.3.0',
        availableVersion: '1.0.4.0',
        state: 'available',
        detail: null,
        bootedPcs: 6,
        lastUpdate: { fromVersion: '1.0.2.0', toVersion: '1.0.3.0', outcome: 'installed', detail: null, at: hoursAgo(24 * 3) },
      },
    },
  ] as BootServer[];
}

function version(imageId: string, serial: number, number: number, over: Partial<BootImageVersion> = {}): BootImageVersion {
  return {
    id: tid('biv', serial),
    imageId,
    number,
    parentVersionId: number === 1 ? null : tid('biv', serial - 1),
    state: 'published',
    sizeBytes: (number === 1 ? 62 : 3 + number) * GB,
    note: null,
    createdBy: MEMBER_ID,
    createdAt: hoursAgo(24 * (10 - number)),
    publishedAt: hoursAgo(24 * (10 - number)),
    preparationReport: number === 1 ? REPORT : null,
    progress: null,
    source: 'capture',
    folderMode: null,
    ...over,
  } as BootImageVersion;
}

function seedImages(mode: BootFakeMode): { images: BootImage[]; versions: BootImageVersion[] } {
  if (mode === 'empty') return { images: [], versions: [] };
  const system = [
    version(BOOT_SYSTEM_IMAGE_ID, 11, 1, { note: 'Fresh Windows 11 with drivers' }),
    version(BOOT_SYSTEM_IMAGE_ID, 12, 2, { note: 'Game launchers updated' }),
    version(BOOT_SYSTEM_IMAGE_ID, 13, 3, { note: 'Valorant and Fortnite updates' }),
    version(BOOT_SYSTEM_IMAGE_ID, 14, 4, { note: 'October Windows updates' }),
  ];
  system[0] = { ...system[0]!, motherboard: BOOT_IMAGE_BOARD } as BootImageVersion;
  const game = [version(BOOT_GAME_IMAGE_ID, 21, 1, { sizeBytes: 5400 * GB, note: 'Launch library', preparationReport: null }), version(BOOT_GAME_IMAGE_ID, 22, 2, { sizeBytes: 5460 * GB, note: 'New season games' })];
  const common = () => ({ venueId: VENUE_ID, nicDriverMode: 'auto', nicFamilies: [] as string[], pendingMergeUpToVersionId: null, openEdit: null, versionInProgress: null, archivedAt: null }) as const;
  // Saved from version 2 of Windows 11 Gaming: a differencing layer over it, until it is made independent.
  const tournament = version(BOOT_TOURNAMENT_IMAGE_ID, 41, 1, { parentVersionId: tid('biv', 12), note: 'Tournament build: overlays off', preparationReport: null, sizeBytes: 6 * GB });
  const second = version(BOOT_SECOND_GAME_IMAGE_ID, 51, 1, { sizeBytes: 820 * GB, note: 'Battle.net games', preparationReport: null });
  const images: BootImage[] = [
    {
      ...common(),
      id: BOOT_SYSTEM_IMAGE_ID,
      name: 'Windows 11 Gaming',
      kind: 'system',
      currentVersionId: system[3]!.id,
      currentVersionNumber: 4,
      defaultForVenue: true,
      appliesToImageIds: null,
      driveLetter: null,
      basedOn: null,
      dependents: [{ imageId: BOOT_TOURNAMENT_IMAGE_ID, name: 'Windows 11 Tournament', onVersionNumber: 2 }],
      classIds: [],
      motherboard: BOOT_IMAGE_BOARD,
    },
    { ...common(), id: BOOT_GAME_IMAGE_ID, name: 'Game library', kind: 'game', currentVersionId: game[1]!.id, currentVersionNumber: 2, defaultForVenue: false, appliesToImageIds: null, driveLetter: 'G', basedOn: null, dependents: [], classIds: [], motherboard: null },
    { ...common(), id: BOOT_DRAFT_IMAGE_ID, name: 'Windows 11 Streaming', kind: 'system', currentVersionId: null, currentVersionNumber: null, defaultForVenue: false, appliesToImageIds: null, driveLetter: null, basedOn: null, dependents: [], classIds: [], motherboard: null },
    {
      ...common(),
      id: BOOT_TOURNAMENT_IMAGE_ID,
      name: 'Windows 11 Tournament',
      kind: 'system',
      currentVersionId: tournament.id,
      currentVersionNumber: 1,
      defaultForVenue: false,
      appliesToImageIds: null,
      driveLetter: null,
      basedOn: { imageId: BOOT_SYSTEM_IMAGE_ID, name: 'Windows 11 Gaming', versionId: tid('biv', 12), versionNumber: 2 },
      dependents: [],
      classIds: PRO_CLASS_ID === '' ? [] : [PRO_CLASS_ID],
      motherboard: BOOT_IMAGE_BOARD,
    },
    { ...common(), id: BOOT_SECOND_GAME_IMAGE_ID, name: 'Battle.net library', kind: 'game', currentVersionId: second.id, currentVersionNumber: 1, defaultForVenue: false, appliesToImageIds: [BOOT_TOURNAMENT_IMAGE_ID], driveLetter: 'H', basedOn: null, dependents: [], classIds: [], motherboard: null },
  ] as BootImage[];
  const draft = version(BOOT_DRAFT_IMAGE_ID, 31, 1, { state: 'capturing', publishedAt: null, sizeBytes: null, preparationReport: null, note: null });
  return { images, versions: [...system, ...game, draft, tournament, second] };
}

/** What the two new PCs report (the Floor's New PCs tray): processor and network card, and when they first came. */
const NEW_PC_FACTS: readonly { readonly cpu: string; readonly nic: string; readonly firstSeenAgoMinutes: number; readonly lastStartAgoMinutes: number }[] = [
  { cpu: 'AMD Ryzen 5 7600 6-Core Processor', nic: '10EC:8125', firstSeenAgoMinutes: 10, lastStartAgoMinutes: 2 },
  { cpu: 'Intel(R) Core(TM) i5-12400F', nic: '8086:125C', firstSeenAgoMinutes: 185, lastStartAgoMinutes: 185 },
];

function seedPcs(mode: BootFakeMode, bootOnly: boolean): BootPc[] {
  if (mode === 'empty') return [];
  const gpu = ['nvidia-r565', 'nvidia-r565', 'nvidia-r565', 'nvidia-r565', 'nvidia-r565', 'amd-24.9', 'amd-24.9', 'nvidia-r560', 'amd-24.9'];
  // Two PCs wait as new (not a station yet), after the PCs on the floor: seven of them, or — a Boot-only venue, where every
  // PC on the floor is of this kind — one for each of the floor's thirteen PCs (its consoles never start from the network).
  const linked = bootOnly ? 13 : 7;
  const ids = Array.from({ length: linked + 2 }, (_, index) => BOOT_PC_IDS[index] ?? tid('bpc', index + 1));
  return ids.map((id, index) => {
    const facts = index < linked ? undefined : NEW_PC_FACTS[index - linked];
    const behind = index === 6;
    // PC 4 was added ahead of its first start; PC 8 is not on the floor yet and has never started either.
    const neverBooted = index === 3;
    // PC 5 was given an image that is still being captured: nothing it can start yet.
    const draftImage = index === 4;
    // The Pro class's PCs (stations 9–13, Boot only) start Windows 11 Tournament, the class's image.
    const pro = index >= 8 && index < linked;
    // PC 3 and the second new PC have another motherboard than the one Windows 11 Gaming was made on.
    const board = index === 2 || index === linked + 1 ? BOOT_OTHER_BOARD : BOOT_IMAGE_BOARD;
    return {
      id,
      venueId: VENUE_ID,
      bootServerId: BOOT_SERVER_ID,
      mac: `00:1A:2B:3C:4D:${(0x10 + index).toString(16).toUpperCase().padStart(2, '0')}`,
      stationId: index < linked ? floorStationId(index + 1) : null,
      imageId: draftImage ? BOOT_DRAFT_IMAGE_ID : null,
      effectiveImageId: draftImage ? BOOT_DRAFT_IMAGE_ID : pro ? BOOT_TOURNAMENT_IMAGE_ID : BOOT_SYSTEM_IMAGE_ID,
      currentVersionNumber: draftImage ? null : pro ? 1 : 4,
      behind,
      gameDiskIds: null,
      writebackPolicy: index === 5 ? 'forceServer' : null,
      lastBoot: neverBooted
        ? null
        : {
            at: facts === undefined ? hoursAgo(1 + index * 2) : new Date(NOW_MS - facts.lastStartAgoMinutes * MINUTE_MS).toISOString(),
            imageId: pro ? BOOT_TOURNAMENT_IMAGE_ID : BOOT_SYSTEM_IMAGE_ID,
            versionId: pro ? tid('biv', 41) : tid('biv', behind ? 13 : 14),
            versionNumber: pro ? 1 : behind ? 3 : 4,
            editBoot: false,
            bootSeconds: 21 + index * 3,
            secureBoot: index !== 5,
            writebackLocation: index === 5 ? 'server' : 'local',
            gpuFamily: gpu[index % gpu.length] ?? null,
            nicPciIds: [facts?.nic ?? '8086:125C'],
            motherboard: board,
            reachedWindows: true,
          },
      firstSeenAt: facts === undefined ? hoursAgo(24 * 9) : new Date(NOW_MS - facts.firstSeenAgoMinutes * MINUTE_MS).toISOString(),
      removedAt: null,
    } as BootPc;
  });
}

/** Health points for a range: the server's row, and one per PC, with PC-06 slow and PC-03 cooling. Integers and tenths only. */
function metrics(from: number, to: number, pcFilter: string | null, pcs: readonly BootPc[]): BootMetricPoint[] {
  const span = Math.max(MINUTE_MS, to - from);
  const step = span > 2 * 24 * 3_600_000 ? 24 * 3_600_000 : 5 * MINUTE_MS;
  const points: BootMetricPoint[] = [];
  for (let at = from, n = 0; at <= to; at += step, n += 1) {
    // A slow swell with a little jitter: integers only.
    const wave = Math.round(5 + 4 * Math.sin(n / 9) + ((n * 7) % 3));
    const iso = new Date(at).toISOString();
    if (pcFilter === null) {
      points.push({ at: iso, bootPcId: null, readOps: 4200 + wave * 90, p50Us: 310, p99Us: 1800, hitPct: 94, bytes: 3 * GB, uplinkPct: 38 + wave * 4, diskQueue: 1 + wave / 10 });
    }
    pcs.forEach((pc, index) => {
      if (pcFilter !== null && pc.id !== pcFilter) return;
      const slow = index === 5;
      const cooling = index === 2;
      points.push({
        at: iso,
        bootPcId: pc.id,
        readOps: 400 + wave * 12 + index * 20,
        p50Us: slow ? 1100 : 280 + index * 15,
        p99Us: slow ? 7400 + wave * 120 : 1400 + wave * 60 + index * 90,
        hitPct: cooling ? 74 + (wave % 3) : slow ? 71 + (wave % 4) : 92 + (wave % 5),
        bytes: (150 + wave * 8) * 1024 ** 2,
        uplinkPct: null,
        diskQueue: null,
      });
    });
  }
  return points;
}

export function createFakeBoot(helpers: BootHelpers, mode: BootFakeMode) {
  const { json, problem } = helpers;
  const seeded = seedImages(mode);
  const state = {
    servers: seedServer(mode),
    images: seeded.images,
    versions: seeded.versions,
    pcs: seedPcs(mode, helpers.bootOnly === true),
    /** What a new PC reported beside its boot (its processor), by boot PC id. */
    cpus: new Map<string, string>(),
    /** Identify: until when each PC was told to show its number. */
    identify: new Map<string, string>(),
    edits: [] as { id: string; imageId: string; versionId: string; bootPcId: string | null }[],
    /** What each station class's PCs start (F-BOOT-27): null is not set. The Pro class starts Windows 11 Tournament. */
    classes: new Map<string, { imageId: string | null; gameDiskIds: string[] | null }>(
      FLOOR_CLASSES.map((klass) => [klass.id, { imageId: klass.id === PRO_CLASS_ID && mode === 'ready' ? BOOT_TOURNAMENT_IMAGE_ID : null, gameDiskIds: null }] as const),
    ),
    /**
     * What a test says the boot server reported about a PC (its live state, `stateSince`, `livePhase`, a blocked game disk
     * letter …), by station: merged over the derived row of `GET …/boot/floor` and `…/floor-states`.
     */
    floorOverrides: new Map<string, Partial<BootFloorPc>>(),
    /** Every wipe, pairing and rollout request, as sent. */
    writes: [] as { readonly route: string; readonly body: unknown }[],
    serial: 100,
  };

  // What each new PC reported beside its boot, in the order they wait on the floor.
  state.pcs.filter((pc) => pc.stationId === null).forEach((pc, index) => {
    const cpu = NEW_PC_FACTS[index]?.cpu;
    if (cpu !== undefined) state.cpus.set(pc.id, cpu);
  });

  const next = (prefix: string): string => {
    state.serial += 1;
    return tid(prefix, state.serial);
  };
  const validation = (issues: readonly { path: readonly PropertyKey[]; code: string; message: string }[]) => ({
    errors: issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
  });
  const need = (permission: string): FakeResponse | undefined => (helpers.can(permission) ? undefined : problem(403, 'forbidden', 'Not allowed'));

  function bodyOf<T>(schema: { safeParse: (value: unknown) => { success: true; data: T } | { success: false; error: { issues: readonly { path: readonly PropertyKey[]; code: string; message: string }[] } } }, input: unknown): { ok: true; data: T } | { ok: false; response: FakeResponse } {
    const parsed = schema.safeParse(input);
    return parsed.success ? { ok: true, data: parsed.data } : { ok: false, response: problem(400, 'validation_failed', 'Invalid request', validation(parsed.error.issues) as Partial<Problem>) };
  }

  const imageOf = (id: string) => state.images.find((image) => image.id === id);
  /** The newest version being captured or prepared, as the api reads it with the list. */
  const withVersionInProgress = (image: BootImage): BootImage => {
    const making = state.versions
      .filter((version) => version.imageId === image.id && (version.state === 'capturing' || version.state === 'preparing'))
      .sort((a, b) => b.number - a.number)[0];
    return { ...image, versionInProgress: making === undefined ? null : { id: making.id, number: making.number, state: making.state, progress: making.progress } };
  };
  const publishedOf = (imageId: string) => state.versions.filter((candidate) => candidate.imageId === imageId).sort((a, b) => b.number - a.number);
  /** The classes that start an image (or see a game disk), as the api reads them with the list. */
  const withClasses = (image: BootImage): BootImage => ({
    ...image,
    classIds: [...state.classes.entries()]
      .filter(([, choice]) => (image.kind === 'system' ? choice.imageId === image.id : (choice.gameDiskIds ?? []).includes(image.id)))
      .map(([classId]) => classId),
  });

  function setCurrent(image: BootImage, target: BootImageVersion): void {
    image.currentVersionId = target.id;
    image.currentVersionNumber = target.number;
    for (const pc of state.pcs) {
      if ((pc.effectiveImageId ?? pc.imageId) === image.id) {
        pc.currentVersionNumber = target.number;
        pc.behind = pc.lastBoot !== null && pc.lastBoot.versionNumber !== null && pc.lastBoot.versionNumber < target.number;
      }
    }
  }

  /** What a PC starts, and where that comes from: its own image, else its class's, else the venue default (F-BOOT-27). */
  function resolved(pc: BootPc): { readonly image: BootImage | undefined; readonly from: 'pc' | 'class' | 'venue'; readonly klass: { imageId: string | null; gameDiskIds: string[] | null } | undefined } {
    const classId = pc.stationId === null ? undefined : helpers.classOf?.(pc.stationId);
    const klass = classId === undefined ? undefined : state.classes.get(classId);
    if (pc.imageId !== null) return { image: imageOf(pc.imageId), from: 'pc', klass };
    const chosen = klass?.imageId == null ? undefined : imageOf(klass.imageId);
    if (chosen !== undefined && chosen.archivedAt === null) return { image: chosen, from: 'class', klass };
    return { image: state.images.find((one) => one.kind === 'system' && one.defaultForVenue && one.archivedAt === null), from: 'venue', klass };
  }

  /** The motherboard a version was made on: the first recorded walking up its chain. */
  function boardOf(versionId: string | null): BootImageVersion['motherboard'] {
    const seen = new Set<string>();
    for (let id = versionId; id !== null && !seen.has(id); ) {
      seen.add(id);
      const found = state.versions.find((one) => one.id === id);
      if (found === undefined) return null;
      if (found.motherboard != null) return found.motherboard;
      id = found.parentVersionId;
    }
    return null;
  }

  /** Each live PC on the floor with its boot state, derived as the api does (no server, offline, licence, no image, behind). */
  function floorPcs(): BootFloorPc[] {
    return derivedFloorPcs().map((pc) => ({ ...pc, ...(state.floorOverrides.get(pc.stationId) ?? {}) }));
  }

  function derivedFloorPcs(): BootFloorPc[] {
    const server = state.servers[0];
    const editing = new Set(state.images.flatMap((image) => (image.openEdit?.bootPcId == null ? [] : [image.openEdit.bootPcId])));
    return state.pcs.flatMap((pc): BootFloorPc[] => {
      if (pc.stationId === null || pc.removedAt !== null) return [];
      const { image, from, klass } = resolved(pc);
      const behindNow = pc.lastBoot !== null && image !== undefined && image.currentVersionId !== null && !pc.lastBoot.editBoot && pc.lastBoot.versionId !== image.currentVersionId;
      const problem: BootFloorPc['problem'] =
        server === undefined || server.status === 'pairing'
          ? 'no_boot_server'
          : server.status === 'offline'
            ? 'boot_server_offline'
            : server.licenceState === 'expired'
              ? 'licence_expired'
              : image === undefined || image.currentVersionId === null
                ? 'no_image'
                : undefined;
      const last = pc.lastBoot;
      const state: BootFloorPc['state'] = problem !== undefined ? 'cant_start' : behindNow ? 'update_waiting' : last === null ? 'never_started' : 'ready';
      const disksFrom: 'pc' | 'class' | 'venue' = pc.gameDiskIds !== null ? 'pc' : klass?.gameDiskIds != null ? 'class' : 'venue';
      const disks = (pc.gameDiskIds ?? klass?.gameDiskIds ?? stateGameDisks(image?.id ?? null)).flatMap((id) => {
        const disk = imageOf(id);
        return disk === undefined ? [] : [{ id: disk.id, name: disk.name, driveLetter: disk.driveLetter }];
      });
      return [
        {
          bootPcId: pc.id,
          stationId: pc.stationId,
          mac: pc.mac,
          state,
          ...(problem === undefined ? {} : { problem }),
          editing: editing.has(pc.id),
          image:
            image === undefined
              ? null
              : {
                  id: image.id,
                  name: image.name,
                  own: from === 'pc',
                  from,
                  versionNumber: last?.editBoot === true ? null : (last?.versionNumber ?? null),
                  currentVersionNumber: image.currentVersionNumber,
                  latest: last !== null && !behindNow && image.currentVersionNumber !== null && last.versionNumber === image.currentVersionNumber,
                },
          gameDisks: disks,
          lastStartAt: last?.at ?? null,
          bootSeconds: last?.bootSeconds ?? null,
          secureBoot: last?.secureBoot ?? null,
          gpuFamily: last?.gpuFamily ?? null,
          writebackLocation: last?.writebackLocation ?? null,
          writebackPolicy: pc.writebackPolicy,
          gameDisksFrom: disksFrom,
          ownImageId: pc.imageId,
          ownGameDiskIds: pc.gameDiskIds,
          motherboard: last?.motherboard ?? null,
          imageMotherboard: image === undefined ? null : boardOf(image.currentVersionId),
        } as BootFloorPc,
      ];
    });
  }

  /** The game disks a PC with no list of its own sees: those for its image, or for every image. */
  function stateGameDisks(imageId: string | null): string[] {
    if (imageId === null) return [];
    return state.images.filter((one) => one.kind === 'game' && one.archivedAt === null && (one.appliesToImageIds === null || one.appliesToImageIds.includes(imageId))).map((one) => one.id);
  }

  /** The live boot PCs with no station, newest first, as the Floor's New PCs tray reads them. */
  function newPcs(): BootNewPc[] {
    return state.pcs
      .filter((pc) => pc.stationId === null && pc.removedAt === null)
      .sort((a, b) => b.firstSeenAt.localeCompare(a.firstSeenAt))
      .map((pc) => {
        const until = state.identify.get(pc.id);
        return {
          bootPcId: pc.id,
          mac: pc.mac,
          gpuFamily: pc.lastBoot?.gpuFamily ?? null,
          cpu: state.cpus.get(pc.id) ?? null,
          nicPciIds: pc.lastBoot?.nicPciIds ?? [],
          firstSeenAt: pc.firstSeenAt,
          lastStartAt: pc.lastBoot?.at ?? null,
          identifyUntil: until !== undefined && Date.parse(until) > Date.now() ? until : null,
          motherboard: pc.lastBoot?.motherboard ?? null,
          imageName: resolved(pc).image?.name ?? null,
          imageMotherboard: boardOf(resolved(pc).image?.currentVersionId ?? null),
        } as BootNewPc;
      });
  }

  function handle(method: string, pathname: string, searchParams: URLSearchParams, input: Record<string, unknown>, headers: Readonly<Record<string, string>> = {}): FakeResponse | undefined {
    if (!pathname.startsWith(`${BASE}/`)) return undefined;
    const gated = helpers.gate();
    if (gated !== undefined) return gated;
    const path = pathname.slice(BASE.length + 1);
    const isRead = method === 'GET';
    if (mode === 'unentitled') {
      return problem(402, 'entitlement-required', 'Clavian Boot is not part of this organisation’s plan.');
    }

    if (path === 'servers' && isRead) return need('boot:view') ?? json(200, { servers: state.servers });
    if (path === 'servers/pairing-code' && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const parsed = bodyOf(CreateBootServerPairingCodeSchema, input);
      if (!parsed.ok) return parsed.response;
      state.writes.push({ route: 'pairing-code', body: input });
      if (state.servers.length === 0) {
        state.servers = [{ id: BOOT_SERVER_ID, venueId: VENUE_ID, name: parsed.data.name ?? 'Boot server', status: 'pairing', version: null, pairedAt: null, lastSeenAt: null, disks: [], licenceState: null, ramCacheBytes: null, update: null } as BootServer];
      }
      return json(201, { bootServerId: state.servers[0]!.id, code: 'K7M4Q9XD', expiresAt: new Date(Date.parse(NOW) + 30 * MINUTE_MS).toISOString() });
    }
    const server = /^servers\/([^/]+)(?:\/(unpair|update))?$/.exec(path);
    if (server !== null) {
      const found = state.servers.find((one) => one.id === server[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot server at this venue.');
      if (server[2] === 'update' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(UpdateBootServerSoftwareSchema, input);
        if (!parsed.ok) return parsed.response;
        const waiting = found.update?.availableVersion ?? null;
        if (found.status !== 'online' || waiting === null || found.update?.state === 'installing') {
          return problem(409, 'conflict', 'The boot server cannot install an update now.', { detail: 'The boot server has no update downloaded and verified to install.' });
        }
        state.writes.push({ route: 'update', body: input });
        found.update = { ...found.update!, state: 'installing', detail: null };
        return json(202, { bootServerId: found.id, version: waiting, force: parsed.data.force, requestedAt: NOW });
      }
      if (server[2] === 'unpair' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        found.status = 'pairing';
        found.licenceState = null;
        return json(200, found);
      }
      if (server[2] === undefined && method === 'PATCH') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(UpdateBootServerSchema, input);
        if (!parsed.ok) return parsed.response;
        found.name = parsed.data.name;
        return json(200, found);
      }
    }

    if (path === 'images' && isRead) return need('boot:view') ?? json(200, { images: state.images.map(withVersionInProgress).map(withClasses) });
    if (path === 'images' && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const parsed = bodyOf(CreateBootImageSchema, input);
      if (!parsed.ok) return parsed.response;
      const held = new Set(state.images.filter((one) => one.kind === 'game' && one.archivedAt === null).map((one) => one.driveLetter));
      if (parsed.data.driveLetter !== undefined && held.has(parsed.data.driveLetter)) return problem(409, 'conflict', `${parsed.data.driveLetter}: is another game disk’s drive letter.`);
      const driveLetter = parsed.data.kind !== 'game' ? null : (parsed.data.driveLetter ?? 'GHIJKLMNOPQRSTUVWXYZDEF'.split('').find((letter) => !held.has(letter)) ?? null);
      const image = {
        id: next('bim'),
        venueId: VENUE_ID,
        name: parsed.data.name,
        kind: parsed.data.kind,
        currentVersionId: null,
        currentVersionNumber: null,
        defaultForVenue: parsed.data.defaultForVenue ?? false,
        nicDriverMode: parsed.data.nicDriverMode ?? 'auto',
        nicFamilies: parsed.data.nicFamilies ?? [],
        appliesToImageIds: null,
        driveLetter,
        pendingMergeUpToVersionId: null,
        openEdit: null,
        versionInProgress: null,
        archivedAt: null,
      } as BootImage;
      const first = {
        id: next('biv'),
        imageId: image.id,
        number: 1,
        parentVersionId: null,
        state: parsed.data.source === 'capture' ? 'capturing' : 'preparing',
        sizeBytes: null,
        note: null,
        createdBy: MEMBER_ID,
        createdAt: NOW,
        publishedAt: null,
        preparationReport: null,
        progress: null,
        source: parsed.data.source,
        folderMode: parsed.data.source === 'folder' ? (parsed.data.folderMode ?? 'copy') : null,
      } as BootImageVersion;
      state.images.push(image);
      state.versions.push(first);
      state.writes.push({ route: 'images', body: input });
      return json(201, {
        image: withVersionInProgress(image),
        version: first,
        captureCode: parsed.data.source === 'capture' ? 'R4TN82HW' : null,
        captureCodeExpiresAt: parsed.data.source === 'capture' ? new Date(Date.parse(NOW) + 60 * MINUTE_MS).toISOString() : null,
      });
    }
    const imageRoute = /^images\/([^/]+)(?:\/(versions|restore|merge|prepare|edits|server-edits|make-independent))?$/.exec(path);
    if (imageRoute !== null) {
      const image = imageOf(imageRoute[1]!);
      if (image === undefined) return problem(404, 'not-found', 'No such image at this venue.');
      const sub = imageRoute[2];
      if (sub === undefined && method === 'PATCH') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(UpdateBootImageSchema, input);
        if (!parsed.ok) return parsed.response;
        const { archived, ...rest } = parsed.data;
        if (rest.driveLetter !== undefined && rest.driveLetter !== image.driveLetter) {
          if (image.kind !== 'game') return problem(400, 'validation-failed', 'Only a game disk has a drive letter.');
          if (image.openEdit?.target === 'server') return problem(409, 'conflict', 'The game disk is open for updates on the server on its current letter.');
          const holder = state.images.find((one) => one.id !== image.id && one.kind === 'game' && one.archivedAt === null && one.driveLetter === rest.driveLetter);
          if (holder !== undefined) return problem(409, 'conflict', `${rest.driveLetter}: is the drive letter of the game disk “${holder.name}”.`);
        }
        Object.assign(image, rest);
        if (archived !== undefined) image.archivedAt = archived ? NOW : null;
        if (parsed.data.defaultForVenue === true) for (const other of state.images) if (other.id !== image.id && other.kind === 'system') other.defaultForVenue = false;
        return json(200, image);
      }
      if (sub === 'versions' && isRead) return need('boot:view') ?? json(200, { versions: publishedOf(image.id) });
      if (sub === 'restore' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(RestoreBootImageSchema, input);
        if (!parsed.ok) return parsed.response;
        const target = state.versions.find((one) => one.id === parsed.data.versionId && one.imageId === image.id && one.state === 'published');
        if (target === undefined) return problem(409, 'conflict', 'That version is not a restore point.');
        setCurrent(image, target);
        state.writes.push({ route: 'restore', body: input });
        return json(200, image);
      }
      if (sub === 'prepare' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(PrepareBootImageVersionSchema, input);
        if (!parsed.ok) return parsed.response;
        const target = state.versions.find((one) => one.id === parsed.data.versionId && one.imageId === image.id);
        if (target === undefined) return problem(404, 'not-found', 'No such version of this image.');
        if (target.state === 'published' && target.parentVersionId === null) {
          // A published version is left alone: a new version is made from it.
          const made = {
            ...target,
            id: next('biv'),
            number: Math.max(...state.versions.filter((one) => one.imageId === image.id).map((one) => one.number)) + 1,
            parentVersionId: target.id,
            state: 'preparing',
            sizeBytes: null,
            createdAt: NOW,
            publishedAt: null,
            preparationReport: null,
            progress: null,
          } as BootImageVersion;
          state.versions.push(made);
          state.writes.push({ route: 'prepare', body: input });
          return json(202, made);
        }
        // A game disk made from a folder whose job failed has no report: the folder job runs again (Move the rest).
        const failedFolder = target.state === 'failed' && target.source === 'folder';
        if (!failedFolder && (target.state !== 'failed' || target.preparationReport === null)) return problem(409, 'conflict', 'Only a captured or imported version that failed or is published can be prepared again.');
        target.state = 'preparing';
        state.writes.push({ route: 'prepare', body: input });
        return json(202, target);
      }
      if (sub === 'merge' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        const parsed = bodyOf(MergeBootImageSchema, input);
        if (!parsed.ok) return parsed.response;
        const target = state.versions.find((one) => one.id === parsed.data.upToVersionId && one.imageId === image.id);
        if (target === undefined) return problem(404, 'not-found', 'No such version.');
        image.pendingMergeUpToVersionId = target.id;
        state.writes.push({ route: 'merge', body: input });
        return json(202, image);
      }
      if (sub === 'make-independent' && method === 'POST') {
        const denied = need('boot:manage');
        if (denied) return denied;
        if (image.basedOn == null) return problem(409, 'conflict', 'This image is not built on another image.');
        const base = state.versions.filter((one) => one.imageId === image.id).sort((a, b) => a.number - b.number)[0];
        image.pendingMergeUpToVersionId = base?.id ?? null;
        state.writes.push({ route: 'make-independent', body: { imageId: image.id } });
        return json(202, withClasses(image));
      }
      if (sub === 'edits' && method === 'POST') {
        const denied = need('boot:edit_image');
        if (denied) return denied;
        const parsed = bodyOf(StartBootEditSchema, input);
        if (!parsed.ok) return parsed.response;
        if (image.openEdit !== null) return problem(409, 'boot-edit-in-progress', 'This image is already being edited.');
        const draft = {
          id: next('biv'),
          imageId: image.id,
          number: (publishedOf(image.id)[0]?.number ?? 0) + 1,
          parentVersionId: image.currentVersionId,
          state: 'editing',
          sizeBytes: null,
          note: null,
          createdBy: MEMBER_ID,
          createdAt: NOW,
          publishedAt: null,
          preparationReport: null,
          progress: null,
        } as BootImageVersion;
        state.versions.push(draft);
        const edit = { id: next('bed'), imageId: image.id, versionId: draft.id, target: 'pc', bootPcId: parsed.data.bootPcId, mountedAt: null, detail: null, startedBy: MEMBER_ID, startedAt: NOW, endedAt: null, outcome: null };
        image.openEdit = edit as BootImage['openEdit'];
        state.edits.push(edit);
        state.writes.push({ route: 'edits', body: input });
        return json(201, edit);
      }
      if (sub === 'server-edits' && method === 'POST') {
        const denied = need('boot:edit_image');
        if (denied) return denied;
        if (image.kind !== 'game') return problem(409, 'conflict', 'Only a game disk is updated on the boot server.');
        if (image.openEdit !== null) return problem(409, 'boot-edit-in-progress', 'This game disk is already being edited.');
        const draft = {
          id: next('biv'),
          imageId: image.id,
          number: (publishedOf(image.id)[0]?.number ?? 0) + 1,
          parentVersionId: image.currentVersionId,
          state: 'editing',
          sizeBytes: null,
          note: null,
          createdBy: MEMBER_ID,
          createdAt: NOW,
          publishedAt: null,
          preparationReport: null,
          progress: null,
        } as BootImageVersion;
        state.versions.push(draft);
        // The fake boot server mounts it at once.
        const edit = { id: next('bed'), imageId: image.id, versionId: draft.id, target: 'server', bootPcId: null, mountedAt: NOW, detail: null, startedBy: MEMBER_ID, startedAt: NOW, endedAt: null, outcome: null };
        image.openEdit = edit as BootImage['openEdit'];
        state.edits.push(edit);
        state.writes.push({ route: 'server-edits', body: input });
        return json(201, edit);
      }
    }
    const edit = /^edits\/([^/]+)\/(publish|discard)$/.exec(path);
    if (edit !== null && method === 'POST') {
      const denied = need('boot:edit_image');
      if (denied) return denied;
      const image = state.images.find((one) => one.openEdit?.id === edit[1]);
      if (image === undefined || image.openEdit === null) return problem(404, 'not-found', 'No such edit.');
      const draft = state.versions.find((one) => one.id === image.openEdit?.versionId);
      const open = image.openEdit;
      if (edit[2] === 'publish') {
        const parsed = bodyOf(PublishBootEditSchema, input);
        if (!parsed.ok) return parsed.response;
        if (parsed.data.asNewImage !== undefined && draft !== undefined) {
          // Saved as a new image: version 1 of it, standing on the version the change was made on.
          if (open.target === 'server' || image.kind !== 'system') return problem(409, 'conflict', 'Only a change of a Windows image made on a PC can be saved as a new image.');
          const made = {
            ...image,
            id: next('bim'),
            name: parsed.data.asNewImage.name,
            defaultForVenue: false,
            currentVersionId: draft.id,
            currentVersionNumber: 1,
            pendingMergeUpToVersionId: null,
            openEdit: null,
            basedOn: { imageId: image.id, name: image.name, versionId: draft.parentVersionId ?? '', versionNumber: state.versions.find((one) => one.id === draft.parentVersionId)?.number ?? 1 },
            dependents: [],
            classIds: [],
          } as BootImage;
          Object.assign(draft, { imageId: made.id, number: 1, state: 'published', publishedAt: NOW, note: parsed.data.note === '' ? null : parsed.data.note, sizeBytes: 4 * GB });
          state.images.push(made);
          image.openEdit = null;
          state.writes.push({ route: 'edit-publish', body: input });
          return json(200, { ...open, endedAt: NOW, outcome: 'published' });
        }
        if (draft !== undefined) {
          draft.state = 'published';
          draft.publishedAt = NOW;
          draft.note = parsed.data.note === '' ? null : parsed.data.note;
          draft.sizeBytes = 4 * GB;
          setCurrent(image, draft);
        }
      } else if (draft !== undefined) {
        draft.state = 'discarded';
      }
      image.openEdit = null;
      state.writes.push({ route: `edit-${edit[2]}`, body: input });
      return json(200, { ...open, endedAt: NOW, outcome: edit[2] === 'publish' ? 'published' : 'discarded' });
    }

    if (path === 'floor' && isRead) return need('boot:view') ?? json(200, { pcs: floorPcs() });
    // The simple state for every member who sees the Floor: the state and reason code, nothing more.
    if (path === 'floor-states' && isRead) {
      return need('floor:read') ?? json(200, { pcs: floorPcs().map((pc) => ({ stationId: pc.stationId, state: pc.state, ...(pc.problem === undefined ? {} : { problem: pc.problem }) })) });
    }
    if (path === 'classes' && isRead) {
      return need('boot:view') ?? json(200, { classes: [...state.classes.entries()].map(([classId, choice]) => ({ classId, imageId: choice.imageId, gameDiskIds: choice.gameDiskIds })) });
    }
    const classRoute = /^classes\/([^/]+)$/.exec(path);
    if (classRoute !== null && method === 'PUT') {
      const denied = need('boot:manage') ?? need('stations:manage');
      if (denied) return denied;
      const parsed = bodyOf(UpdateBootClassSchema, input);
      if (!parsed.ok) return parsed.response;
      const classId = classRoute[1]!;
      if (!state.classes.has(classId)) return problem(404, 'not-found', 'No such station class at this venue.');
      const image = parsed.data.imageId === null ? undefined : imageOf(parsed.data.imageId);
      if (parsed.data.imageId !== null && (image === undefined || image.kind !== 'system' || image.archivedAt !== null)) return problem(400, 'validation-failed', 'Choose a Windows image of this venue that is not archived.');
      const choice = { imageId: parsed.data.imageId, gameDiskIds: parsed.data.gameDiskIds === null ? null : [...parsed.data.gameDiskIds] };
      state.classes.set(classId, choice);
      state.writes.push({ route: 'class', body: { classId, ...parsed.data } });
      return json(200, { classId, ...choice });
    }
    if (path === 'pcs' && isRead) return need('boot:view') ?? json(200, { pcs: state.pcs.filter((one) => one.removedAt === null) });
    if (path === 'new-pcs' && isRead) return need('boot:view') ?? json(200, { pcs: newPcs() });
    const identifyPc = /^pcs\/([^/]+)\/identify$/.exec(path);
    if (identifyPc !== null && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const found = state.pcs.find((one) => one.id === identifyPc[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot PC at this venue.');
      const parsed = bodyOf(IdentifyBootPcSchema, input);
      if (!parsed.ok) return parsed.response;
      if (found.removedAt !== null || state.servers[0]?.status !== 'online') return problem(409, 'conflict', 'The boot server is not online.');
      const until = new Date(Date.now() + 60_000).toISOString();
      state.identify.set(found.id, until);
      state.writes.push({ route: 'identify', body: { bootPcId: found.id, ...parsed.data } });
      return json(202, { bootPcId: found.id, label: parsed.data.label, until });
    }
    const makePc = /^pcs\/([^/]+)\/make-station$/.exec(path);
    if (makePc !== null && method === 'POST') {
      const denied = need('boot:manage') ?? need('stations:manage');
      if (denied) return denied;
      const found = state.pcs.find((one) => one.id === makePc[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot PC at this venue.');
      const parsed = bodyOf(MakeBootPcStationSchema, input);
      if (!parsed.ok) return parsed.response;
      if (found.stationId !== null || found.removedAt !== null) return problem(409, 'conflict', 'This PC is already a station.');
      const made = helpers.makeStation?.({
        number: parsed.data.number,
        classId: parsed.data.classId,
        ...(parsed.data.zoneId === undefined ? {} : { zoneId: parsed.data.zoneId }),
        ...(parsed.data.name === undefined ? {} : { name: parsed.data.name }),
      });
      if (made === undefined) return problem(404, 'not-found', 'No station can be made here.');
      if ('conflict' in made) return problem(409, 'conflict', made.conflict);
      if ('missing' in made) return problem(404, 'not-found', made.missing);
      found.stationId = made.station.id;
      state.identify.delete(found.id);
      state.writes.push({ route: 'make-station', body: { bootPcId: found.id, ...parsed.data, idempotencyKey: headers['idempotency-key'] ?? null } });
      return json(201, { station: made.station, pc: found });
    }
    const removePc = /^pcs\/([^/]+)\/remove$/.exec(path);
    if (removePc !== null && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const found = state.pcs.find((one) => one.id === removePc[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot PC at this venue.');
      found.removedAt = NOW;
      state.writes.push({ route: 'pc-remove', body: { bootPcId: found.id } });
      return json(200, found);
    }
    const pc = /^pcs\/([^/]+)$/.exec(path);
    if (pc !== null && method === 'PATCH') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const found = state.pcs.find((one) => one.id === pc[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot PC at this venue.');
      const parsed = bodyOf(UpdateBootPcSchema, input);
      if (!parsed.ok) return parsed.response;
      Object.assign(found, parsed.data);
      if (parsed.data.imageId !== undefined) {
        found.effectiveImageId = parsed.data.imageId ?? state.images.find((image) => image.defaultForVenue)?.id ?? null;
        found.currentVersionNumber = imageOf(found.effectiveImageId ?? '')?.currentVersionNumber ?? null;
      }
      state.writes.push({ route: 'pc', body: input });
      return json(200, found);
    }
    const seed = /^pcs\/([^/]+)\/shader-seed$/.exec(path);
    if (seed !== null && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const found = state.pcs.find((one) => one.id === seed[1]);
      if (found === undefined) return problem(404, 'not-found', 'No such boot PC at this venue.');
      const family = found.lastBoot?.gpuFamily ?? null;
      if (family === null) return problem(409, 'conflict', 'The PC has not reported its GPU family: its shader cache has no family seed to become.');
      state.writes.push({ route: 'shader-seed', body: { bootPcId: found.id } });
      return json(202, { bootPcId: found.id, gpuFamily: family, requestedAt: NOW });
    }
    if (path === 'wipes' && method === 'POST') {
      const denied = need('boot:wipe');
      if (denied) return denied;
      const parsed = bodyOf(CreateBootWipeSchema, input);
      if (!parsed.ok) return parsed.response;
      state.writes.push({ route: 'wipes', body: input });
      return json(202, { kind: parsed.data.kind, scope: parsed.data.scope, bootPcId: parsed.data.bootPcId ?? null, gpuFamily: parsed.data.gpuFamily ?? null, reason: parsed.data.reason, shaderWipeId: null, requestedAt: NOW });
    }
    if (path === 'rollouts/restart-idle' && method === 'POST') {
      const denied = need('boot:manage');
      if (denied) return denied;
      const parsed = bodyOf(RestartIdleBootPcsSchema, input);
      if (!parsed.ok) return parsed.response;
      const only = parsed.data.bootPcIds === undefined ? undefined : new Set<string>(parsed.data.bootPcIds);
      const behind = state.pcs.filter((one) => one.behind && one.effectiveImageId === parsed.data.imageId && (only === undefined || only.has(one.id)));
      state.writes.push({ route: 'restart-idle', body: input });
      return json(200, {
        imageId: parsed.data.imageId,
        currentVersionNumber: imageOf(parsed.data.imageId)?.currentVersionNumber ?? 4,
        restarted: behind.map((one) => ({ bootPcId: one.id, stationId: one.stationId ?? floorStationId(1), commandId: next('cmd') })),
        skipped: [],
      });
    }
    if (path === 'metrics' && isRead) {
      const denied = need('boot:view');
      if (denied) return denied;
      const from = Date.parse(searchParams.get('from') ?? '');
      const to = Date.parse(searchParams.get('to') ?? '');
      if (Number.isNaN(from) || Number.isNaN(to)) return problem(400, 'validation_failed', 'from and to are required');
      const points = mode === 'empty' ? [] : metrics(from, to, searchParams.get('pc'), state.pcs);
      return json(200, { granularity: to - from > 2 * 24 * 3_600_000 ? 'day' : 'minute', points });
    }
    return problem(404, 'not-found', 'No such route');
  }

  return { handle, state };
}
